/**
 * Pure TEA update for EntityFieldsPanel dialog chrome (one Model).
 *
 * Custom-type and new-attribute drafts are nested; opening one clears the other.
 * Shell: `EntityFieldsPanel.vue` (single `useTea`).
 */
import { assertNever } from "../lib/assertNever";
import type { UpdateFn } from "../lib/tea";
import type { ZwDatabaseFieldKind } from "../lib/zwConfig";

export type CustomTypeDialogModel = {
  readonly open: boolean;
  readonly draft: string;
};

export const initialCustomTypeDialogModel: CustomTypeDialogModel = {
  open: false,
  draft: "",
};

export type NewAttributeDialogModel = {
  readonly open: boolean;
  readonly name: string;
  readonly kind: ZwDatabaseFieldKind;
  readonly optionsText: string;
};

export const initialNewAttributeDialogModel: NewAttributeDialogModel = {
  open: false,
  name: "",
  kind: "text",
  optionsText: "",
};

export type EntityFieldsPanelDialogsModel = {
  readonly customType: CustomTypeDialogModel;
  readonly newAttribute: NewAttributeDialogModel;
  readonly titleDraft: string;
  readonly fieldsOpen: boolean;
  /** Keys of attributes the author just added this session (show even if empty). */
  readonly addedAttrKeys: ReadonlySet<string>;
};

export function initialEntityFieldsPanelDialogsModel(
  fieldsOpen = false,
): EntityFieldsPanelDialogsModel {
  return {
    customType: initialCustomTypeDialogModel,
    newAttribute: initialNewAttributeDialogModel,
    titleDraft: "",
    fieldsOpen,
    addedAttrKeys: new Set(),
  };
}

export type CustomTypeDialogMsg =
  | { readonly type: "Open"; readonly draft: string }
  | { readonly type: "DraftChanged"; readonly draft: string }
  | { readonly type: "Commit" }
  | { readonly type: "Cancel" };

export type NewAttributeDialogMsg =
  | { readonly type: "Open" }
  | { readonly type: "Cancel" }
  | { readonly type: "NameChanged"; readonly name: string }
  | { readonly type: "KindChanged"; readonly kind: ZwDatabaseFieldKind }
  | { readonly type: "OptionsTextChanged"; readonly optionsText: string }
  | { readonly type: "Commit" };

export type EntityFieldsPanelDialogsMsg =
  | { readonly type: "CustomType"; readonly msg: CustomTypeDialogMsg }
  | { readonly type: "NewAttribute"; readonly msg: NewAttributeDialogMsg }
  | { readonly type: "TitleDraftChanged"; readonly draft: string }
  | { readonly type: "SeedTitle"; readonly title: string }
  | { readonly type: "SetFieldsOpen"; readonly open: boolean }
  | { readonly type: "ToggleFieldsOpen" }
  | { readonly type: "ClearAddedAttrKeys" }
  | { readonly type: "AddAttrKey"; readonly key: string };

export type EntityFieldsPanelDialogsCmd =
  | { readonly kind: "applyType"; readonly value: string }
  | {
      readonly kind: "addField";
      readonly name: string;
      readonly fieldKind: ZwDatabaseFieldKind;
      readonly options: readonly string[];
    }
  | { readonly kind: "persistFieldsOpen"; readonly open: boolean };

function updateCustomType(
  model: CustomTypeDialogModel,
  msg: CustomTypeDialogMsg,
): readonly [CustomTypeDialogModel, readonly EntityFieldsPanelDialogsCmd[]] {
  switch (msg.type) {
    case "Open":
      return [{ open: true, draft: msg.draft }, []];
    case "DraftChanged":
      return [{ ...model, draft: msg.draft }, []];
    case "Cancel":
      return [initialCustomTypeDialogModel, []];
    case "Commit": {
      const value = model.draft.trim();
      if (value.length === 0) {
        return [initialCustomTypeDialogModel, []];
      }
      return [initialCustomTypeDialogModel, [{ kind: "applyType", value }]];
    }
    default:
      return assertNever(msg);
  }
}

function updateNewAttribute(
  model: NewAttributeDialogModel,
  msg: NewAttributeDialogMsg,
): readonly [NewAttributeDialogModel, readonly EntityFieldsPanelDialogsCmd[]] {
  switch (msg.type) {
    case "Open":
      return [{ open: true, name: "", kind: "text", optionsText: "" }, []];
    case "Cancel":
      return [initialNewAttributeDialogModel, []];
    case "NameChanged":
      return [{ ...model, name: msg.name }, []];
    case "KindChanged":
      return [{ ...model, kind: msg.kind }, []];
    case "OptionsTextChanged":
      return [{ ...model, optionsText: msg.optionsText }, []];
    case "Commit": {
      const name = model.name.trim();
      if (name.length === 0) return [model, []];
      const options =
        model.kind === "select"
          ? model.optionsText
              .split(",")
              .map((o) => o.trim())
              .filter((o) => o.length > 0)
          : [];
      return [
        initialNewAttributeDialogModel,
        [
          {
            kind: "addField",
            name,
            fieldKind: model.kind,
            options,
          },
        ],
      ];
    }
    default:
      return assertNever(msg);
  }
}

export function updateEntityFieldsPanelDialogs(
  model: EntityFieldsPanelDialogsModel,
  msg: EntityFieldsPanelDialogsMsg,
): readonly [
  EntityFieldsPanelDialogsModel,
  readonly EntityFieldsPanelDialogsCmd[],
] {
  switch (msg.type) {
    case "CustomType": {
      if (msg.msg.type === "Open") {
        const [customType, cmds] = updateCustomType(
          initialCustomTypeDialogModel,
          msg.msg,
        );
        return [
          {
            ...model,
            customType,
            newAttribute: initialNewAttributeDialogModel,
          },
          cmds,
        ];
      }
      const [customType, cmds] = updateCustomType(model.customType, msg.msg);
      return [{ ...model, customType }, cmds];
    }
    case "NewAttribute": {
      if (msg.msg.type === "Open") {
        const [newAttribute, cmds] = updateNewAttribute(
          initialNewAttributeDialogModel,
          msg.msg,
        );
        return [
          {
            ...model,
            customType: initialCustomTypeDialogModel,
            newAttribute,
          },
          cmds,
        ];
      }
      const [newAttribute, cmds] = updateNewAttribute(
        model.newAttribute,
        msg.msg,
      );
      return [{ ...model, newAttribute }, cmds];
    }
    case "TitleDraftChanged":
      return [{ ...model, titleDraft: msg.draft }, []];
    case "SeedTitle":
      return [{ ...model, titleDraft: msg.title }, []];
    case "SetFieldsOpen":
      return [
        { ...model, fieldsOpen: msg.open },
        [{ kind: "persistFieldsOpen", open: msg.open }],
      ];
    case "ToggleFieldsOpen": {
      const open = model.fieldsOpen === false;
      return [
        { ...model, fieldsOpen: open },
        [{ kind: "persistFieldsOpen", open }],
      ];
    }
    case "ClearAddedAttrKeys":
      return [{ ...model, addedAttrKeys: new Set() }, []];
    case "AddAttrKey": {
      const addedAttrKeys = new Set(model.addedAttrKeys);
      addedAttrKeys.add(msg.key);
      return [{ ...model, addedAttrKeys }, []];
    }
    default:
      return assertNever(msg);
  }
}

export const updateEntityFieldsPanelDialogsFn: UpdateFn<
  EntityFieldsPanelDialogsModel,
  EntityFieldsPanelDialogsMsg,
  EntityFieldsPanelDialogsCmd
> = updateEntityFieldsPanelDialogs;
